import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  useGetCommentLikersQuery,
  useGetDiscussionLikersQuery,
} from "@/redux/service";
import UserAvatar from "@/domains/users/components/UserAvatar";
import LikedByModal from "@/domains/community/components/LikedByModal";
import { LikedByPreviewProps } from "@/domains/community/types";

import "../styles.css";
import { UserWithoutOrganizationResponse } from "@/redux/users/types";

const MAX_VISIBLE_AVATARS = 3;

const LikedByPreview = ({ entity, id, likesCount }: LikedByPreviewProps) => {
  const { t } = useTranslation();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const skip = likesCount === 0;
  const discussionQuery = useGetDiscussionLikersQuery(
    { id, page: 1 },
    { skip: skip || entity !== "discussion" },
  );
  const commentQuery = useGetCommentLikersQuery(
    { id, page: 1 },
    { skip: skip || entity !== "comment" },
  );

  const data =
    entity === "discussion" ? discussionQuery.data : commentQuery.data;

  const visibleUsers = useMemo(
    () => (data?.results ?? []).slice(0, MAX_VISIBLE_AVATARS),
    [data],
  );

  if (likesCount === 0 || visibleUsers.length === 0) return null;

  const overflow = likesCount - visibleUsers.length;

  const handleOpen = () => setIsModalOpen(true);

  return (
    <>
      <div
        className="liked-by-preview"
        onClick={handleOpen}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") handleOpen();
        }}
        aria-label={t("community.labels.likedBy")}
      >
        <div className="liked-by-preview__avatars">
          {visibleUsers.map((user: UserWithoutOrganizationResponse) => (
            <UserAvatar
              key={user.id}
              user={user}
              size="small"
              tooltip={user.fullName}
            />
          ))}
        </div>
        {overflow > 0 && (
          <span className="liked-by-preview__overflow">
            {t("community.labels.likedByOthers", { count: overflow })}
          </span>
        )}
      </div>
      <LikedByModal
        entity={entity}
        id={id}
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      />
    </>
  );
};

export default LikedByPreview;
